import React, { FC } from 'react';
import styled from 'styled-components';
import {
  OverlayStyle,
  overlayBackgroundFor,
  usesSplitBlur,
} from './overlayStyles';
import SplitBlurOverlay from './SplitBlurOverlay';

// Renders one protection treatment (vignette tier, scrim, or split blur) for
// an explicitly given style, looked up from the style registry. It reads no
// Redux state, so the parent site (ImageProtectionOverlay, which supplies the
// studio's style) and the admin preview on Settings → Image Protection, which
// has no store, draw every treatment through this one switch. Deterrence only
// — none of these treatments prevent capture.
const Overlay = styled.div<{ $background: string }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  z-index: 2;
  background: ${(props) => props.$background};
`;

interface StyledOverlayProps {
  style: OverlayStyle;
  imageSrc?: string;
}

const StyledOverlay: FC<StyledOverlayProps> = ({ style, imageSrc }) => {
  if (usesSplitBlur(style)) {
    if (imageSrc) {
      return <SplitBlurOverlay />;
    }

    // No <img> at this call site (a CSS background tile). Split blur now
    // blurs the backdrop, so it would work here too, but these tiles have
    // not been checked with it yet: keep the flat wash rather than risk
    // leaving a split-blur studio unprotected.
    return (
      <Overlay
        data-testid="image-protection-overlay"
        data-overlay-style="scrim"
        $background={overlayBackgroundFor('scrim')}
        aria-hidden="true"
      />
    );
  }

  return (
    <Overlay
      data-testid="image-protection-overlay"
      data-overlay-style={style}
      $background={overlayBackgroundFor(style)}
      aria-hidden="true"
    />
  );
};

export default StyledOverlay;
